import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'config/supabase';
import { Subscription } from 'types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  skipOnboardingCheck?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  skipOnboardingCheck = false,
}) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [hasBrandMaterials, setHasBrandMaterials] = useState<boolean | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [checkingBrands, setCheckingBrands] = useState(true);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        setCheckingSubscription(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_active_subscription', { p_user_id: user.id })
          .returns<Subscription>()
          .single();

        setHasSubscription(!error && !!data);
      } catch (err) {
        console.error('Error checking subscription:', err);
        setHasSubscription(false);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [user]);

  useEffect(() => {
    const checkBrandMaterials = async () => {
      if (!user || skipOnboardingCheck) {
        setCheckingBrands(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('brand_materials')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);

        if (error) throw error;
        setHasBrandMaterials(data && data.length > 0);
      } catch (error) {
        console.error('Error checking brand materials:', error);
        setHasBrandMaterials(false);
      } finally {
        setCheckingBrands(false);
      }
    };

    checkBrandMaterials();
  }, [user, skipOnboardingCheck]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while checking subscription (always) and brand materials (if needed)
  if (checkingSubscription || (!skipOnboardingCheck && checkingBrands)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Priority 1: ALWAYS check subscription (even if skipOnboardingCheck=true)
  if (hasSubscription === false && location.pathname !== '/paywall') {
    console.log('[ProtectedRoute] No subscription, redirecting to /paywall');
    return <Navigate to="/paywall" replace />;
  }

  // Priority 2: If has subscription but no brand materials, redirect to onboarding
  // (only check this if skipOnboardingCheck=false)
  if (!skipOnboardingCheck && hasSubscription === true && hasBrandMaterials === false && location.pathname !== '/onboarding') {
    console.log('[ProtectedRoute] No brand materials, redirecting to /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};
